import React, {useState} from 'react';
import {Box, Menu, MenuItem, styled} from '@mui/material';
import {
  Icon,
  Tooltip,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {CONTEXTUAL_MENU_MIN_WIDTH} from '@/components/menu/menuConstants';
import useCreatorRowActions from '../../hooks/use-creator-row-actions';
import type {
  CampaignCreatorCounts,
  CampaignCreatorRow,
} from '../../types/creatorTabTypes';
import styles from './CreatorRowMenu.module.scss';

const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    borderRadius: '24px',
    paddingInline: '8px',
    minWidth: `${CONTEXTUAL_MENU_MIN_WIDTH}px`,
  },
});

const StyledMenuItem = styled(MenuItem)({
  'display': 'flex',
  'gap': '12px',
  'padding': '14px 16px',
  'borderRadius': '8px',
  '&:hover': {
    background: designSystemToken('semantic.bg.tertiary'),
  },
});

interface CreatorRowMenuProps {
  creator: CampaignCreatorRow;
  onActionComplete: (countAdjustment?: Partial<CampaignCreatorCounts>) => void;
}

function CreatorRowMenu({creator, onActionComplete}: CreatorRowMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {menuItems, actionModals} = useCreatorRowActions({
    creator,
    onActionComplete,
    onCloseMenu: handleClose,
  });

  return (
    <>
      <Box
        className={styles.trigger}
        role="button"
        tabIndex={0}
        aria-label={translate('Creator actions')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen(e);
          }
        }}
      >
        <Icon
          size="small"
          appearance="neutral"
          name="Actions-More-Vertical"
          color={designSystemToken('semantic.fg.primary')}
        />
      </Box>
      <StyledMenu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        transformOrigin={{horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
      >
        {menuItems
          .filter((item) => item.visible)
          .map((item) => {
            const menuItem = (
              <StyledMenuItem
                key={item.id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.disabled) return;
                  item.onClick();
                }}
                disabled={item.disabled}
              >
                {item.icon ? (
                  <Icon size="medium" appearance="neutral" name={item.icon} />
                ) : null}
                {item.label}
              </StyledMenuItem>
            );

            return item.tooltip ? (
              <Tooltip
                key={item.id}
                title={item.tooltip}
                placement="left"
                type="dark"
                arrow
              >
                <span>{menuItem}</span>
              </Tooltip>
            ) : (
              menuItem
            );
          })}
      </StyledMenu>
      {actionModals}
    </>
  );
}

export default CreatorRowMenu;
